import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { all_routes } from '../../router/all_routes'
import { downloadCertificate } from '../../../utils/helpers'
import {
  getCBTDetail,
  submitCBTAnswers,
  CBTData,
  CBTQuestion,
  CBTSubmitRequest,
  CBTSubmitResponse,
  CBTUserAnswer,
  flagFinishAttempt,
} from '../../../api/courses'

const CBTTest = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [answers, setAnswers] = useState<number[]>([])
  const [timeLeft, setTimeLeft] = useState(60 * 60) // 60 minutes in seconds
  const [isTestComplete, setIsTestComplete] = useState(false)
  const [testResult, setTestResult] = useState<{
    score: number
    correctAnswers: number
    totalQuestions: number
    isPassed: boolean
    timeUsed: number
    userAnswers?: CBTUserAnswer[]
  } | null>(null)
  const [showCertificateModal, setShowCertificateModal] = useState(false)
  const [certificateName, setCertificateName] = useState('')

  // API data states
  const [cbtData, setCbtData] = useState<CBTData | null>(null)
  const [questions, setQuestions] = useState<CBTQuestion[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [doubtful, setDoubtful] = useState<boolean[]>([])
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false)
  const [showFinishingCourse, setShowFinishingCourse] = useState(false)

  // Fetch CBT data from API
  useEffect(() => {
    const fetchCBTData = async () => {
      if (!id) {
        setError('Course ID not found')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const response = await getCBTDetail(id)
        setCbtData(response.data)
        setQuestions(response.data.questions)

        // Set timer based on persisted start time (so refresh doesn't reset)
        const durationInSeconds = Math.floor(
          parseInt(response.data.duration) / 1000,
        )
        try {
          const startKey = `cbtStartTime:${id}`
          const storedStart = startKey ? localStorage.getItem(startKey) : null
          let startTimestamp = storedStart ? parseInt(storedStart) : NaN
          if (Number.isNaN(startTimestamp)) {
            startTimestamp = Date.now()
            if (startKey) localStorage.setItem(startKey, String(startTimestamp))
          }
          const elapsedSeconds = Math.floor(
            (Date.now() - startTimestamp) / 1000,
          )
          const remaining = Math.max(0, durationInSeconds - elapsedSeconds)
          // if remaining is 0, set to 1 so timer effect triggers auto-submit next tick
          setTimeLeft(remaining === 0 ? 1 : remaining)
        } catch {
          setTimeLeft(durationInSeconds)
        }

        // Initialize answers array
        setAnswers(new Array(response.data.questions.length).fill(-1))

        // Initialize doubtful array
        setDoubtful(new Array(response.data.questions.length).fill(false))
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to load CBT data')
      } finally {
        setLoading(false)
      }
    }

    fetchCBTData()
  }, [id])

  const handleFinishTest = useCallback(async () => {
    if (!questions.length || !cbtData) return

    setSubmitting(true)
    try {
      // Prepare answers data for API
      const answersData: CBTSubmitRequest = {
        course_id: cbtData.id,
        answers: answers
          .map((answerIndex, questionIndex) => {
            if (answerIndex === -1) return null // Skip unanswered questions
            const question = questions[questionIndex]
            const selectedAnswer = question.answers[answerIndex]
            return {
              question_id: question.id,
              answer_id: selectedAnswer.id,
            }
          })
          .filter(Boolean) as { question_id: number; answer_id: number }[],
      }

      // Submit answers to API
      const response: CBTSubmitResponse = await submitCBTAnswers(answersData)

      setIsTestComplete(true)

      // Clear persisted start time on completion
      try {
        if (id) {
          localStorage.removeItem(`cbtStartTime:${id}`)
        }
      } catch { }

      // Calculate is_passed based on score (assuming Number(cbtData.min_passing_score)% is passing)
      const scoreNumber = parseFloat(response.data.score.replace('%', ''))
      const isPassed = scoreNumber >= Number(cbtData.min_passing_score)

      setTestResult({
        score: scoreNumber,
        correctAnswers: response.data.correct_count,
        totalQuestions: response.data.total_questions,
        isPassed,
        timeUsed:
          (cbtData ? Math.floor(parseInt(cbtData.duration) / 1000) : 60 * 60) -
          timeLeft,
        userAnswers: response.data.user_answers,
      })
    } catch (err) {
      console.error('Error submitting CBT answers:', err)
      // Fallback to local calculation if API fails
      let correctAnswers = 0
      answers.forEach((answer, index) => {
        if (answer === 0) {
          // Assuming first answer is correct
          correctAnswers++
        }
      })

      const score = Math.round((correctAnswers / questions.length) * 100)
      const isPassed = score >= Number(cbtData.min_passing_score)

      setTestResult({
        score,
        correctAnswers,
        totalQuestions: questions.length,
        isPassed,
        timeUsed:
          (cbtData ? Math.floor(parseInt(cbtData.duration) / 1000) : 60 * 60) -
          timeLeft,
        userAnswers: [], // Empty array for fallback
      })
    } finally {
      setSubmitting(false)
    }
  }, [answers, timeLeft, questions, cbtData, id])

  // Timer effect
  useEffect(() => {
    if (timeLeft > 0 && !isTestComplete && !loading) {
      const timer = setInterval(() => {
        setTimeLeft((prev) => {
          if (prev <= 1) {
            handleFinishTest()
            return 0
          }
          return prev - 1
        })
      }, 1000)

      return () => clearInterval(timer)
    }
  }, [timeLeft, isTestComplete, loading, handleFinishTest])

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  const handleAnswerSelect = (answerIndex: number) => {
    const newAnswers = [...answers]
    newAnswers[currentQuestion] = answerIndex
    setAnswers(newAnswers)
  }

  const handleNextQuestion = () => {
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1)
    }
  }

  const handlePreviousQuestion = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1)
    }
  }

  const handleGoToDashboard = () => {
    navigate(all_routes.studentCourses)
  }

  const handleReviewTest = () => {
    navigate(all_routes.studentCBTList.replace(':id', id || ''), {
      state: {
        userAnswers: testResult?.userAnswers || [],
      },
    })
  }

  const handleCertificateDownload = async () => {
    // Check if certificate_downloads exists and has a name
    if (cbtData?.certificate_downloads && cbtData.certificate_downloads.name) {
      // Direct download with existing name
      console.log(
        'Downloading certificate with existing name:',
        cbtData.certificate_downloads.name,
      )
      await downloadCertificate(
        cbtData.certificate_downloads.name,
        undefined, // customFileName
        undefined, // pdfUrl
        cbtData.certificate_downloads.name, // userName
        cbtData?.title, // courseTitle
        true, // useAutoGeneration
        id ? parseInt(id) : undefined, // courseId
        undefined,
        true,
        testResult.score.toString(),
        cbtData?.min_passing_score,
      )
    } else {
      // Show modal for first time download (certificate_downloads is null)
      setShowCertificateModal(true)
    }
  }

  const confirmCertificateDownload = async () => {
    setShowCertificateModal(false)
    // Download logic here
    console.log('Downloading certificate with name:', certificateName)
    await downloadCertificate(
      certificateName,
      undefined, // customFileName
      undefined, // pdfUrl
      certificateName, // userName
      cbtData?.title, // courseTitle
      true, // useAutoGeneration
      id ? parseInt(id) : undefined, // courseId
      undefined,
      true,
      testResult.score.toString(),
      cbtData?.min_passing_score,
    )
  }

  const handleSubmitFlagFinish = async () => {
    const courseId = cbtData?.id.toString();
    await flagFinishAttempt({ course_id: courseId });

    // handleGoToDashboard();
    navigate(all_routes.studentCBTList.replace(':id', id || ''), {
      state: {
        userAnswers: testResult?.userAnswers || [],
      },
    })
  }

  const handleRetest = () => {
    navigate(all_routes.studentCBTInstruction.replace(':id', id || ''), {
      state: {
        userAnswers: testResult?.userAnswers || [],
      },
    })
  }

  if (isTestComplete && testResult) {
    return (
      <div className="content">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6">
              <div className="card text-center">
                <div className="card-body">
                  {/* Result Circle */}
                  <div className="mb-4">
                    <div
                      className={`mx-auto rounded-circle d-flex align-items-center justify-content-center ${testResult.isPassed ? 'bg-success' : 'bg-danger'
                        }`}
                      style={{ width: '120px', height: '120px' }}
                    >
                      <h2 className="text-white mb-0">{testResult.score}</h2>
                    </div>
                  </div>

                  {/* Result Message */}
                  <h4
                    className={`mb-3 ${testResult.isPassed ? 'text-success' : 'text-danger'
                      }`}
                  >
                    {testResult.isPassed ? 'Selamat! 🎉' : 'Mohon Maaf 😞'}
                  </h4>

                  {/* Check if Still Have Chance */}
                  {cbtData?.user_attempt_count < Number(cbtData?.max_attempt) ?
                    <p className="mb-4">
                      {testResult.isPassed
                        ? `Kamu telah lulus ujian CBT. Kamu masih memiliki ${Number(cbtData?.max_attempt) - cbtData?.user_attempt_count} kesempatan untuk mengulang. Pilih Ulangi Test untuk mencoba kembali, atau Review Test untuk melihat pembahasan dan mengakhiri kesempatan ujian.`
                        : `Kamu belum lulus ujian CBT. Kamu masih memiliki ${Number(cbtData?.max_attempt) - cbtData?.user_attempt_count} kesempatan untuk mengulang. Pilih Ulangi Test untuk mencoba kembali, atau Review Test untuk melihat pembahasan dan mengakhiri kesempatan ujian.`}
                    </p>
                    :
                    <p className="mb-4">
                      {testResult.isPassed
                        ? 'Anda telah lulus ujian CBT dengan baik. Silakan lanjutkan ke tahapan berikutnya sesuai petunjuk yang tersedia.'
                        : 'Anda belum lulus ujian CBT. Jangan khawatir! Silakan pelajari kembali materi yang tersedia dan coba lagi di kesempatan berikutnya.'}
                    </p>}

                  {/* Score Details */}
                  <div className="row mb-4">
                    <div className="col-6">
                      <div className="border rounded p-3">
                        <h6 className="text-primary mb-1">
                          {testResult.correctAnswers}/
                          {testResult.totalQuestions}
                        </h6>
                        <small className="text-muted">Jawaban Benar</small>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="border rounded p-3">
                        <h6 className="text-warning mb-1">
                          {formatTime(testResult.timeUsed)}
                        </h6>
                        <small className="text-muted">Waktu Digunakan</small>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  {cbtData?.user_attempt_count < Number(cbtData?.max_attempt) ? <div className="d-flex justify-content-center gap-3">
                    <button
                      className="btn btn-info w-100"
                      onClick={handleRetest}
                    >
                      <i className="isax isax-refresh me-2" />
                      Ulangi Test
                    </button>

                    <button
                      className="btn btn-outline-primary w-100"
                      onClick={handleReviewTest}
                    >
                      <i className="isax isax-document-text me-2" />
                      Review Test
                    </button>
                  </div> : <div className="d-flex align-items-center justify-content-center">
                    <button
                      className="btn btn-outline-primary mt-3 w-100"
                      onClick={handleReviewTest}
                    >
                      <i className="isax isax-document-text me-2" />
                      Review Test
                    </button>
                  </div>}

                  <div className="d-flex align-items-center justify-content-center">
                    <button
                      className="btn btn-success mt-3 w-100"
                      onClick={handleCertificateDownload}
                    >
                      <i className="isax isax-document-download me-2" />
                      Download Sertifikat
                    </button>
                  </div>

                  <div className="d-flex align-items-center justify-content-center">
                    <button
                      className="btn btn-primary mt-3 w-100"
                      onClick={handleGoToDashboard}
                    >
                      <i className="isax isax-home me-2" />
                      Kembali ke Dashboard
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Submit Confirmation Modal */}
        {showFinishingCourse && (
          <>
            <div className="modal-backdrop fade show"></div>
            <div
              className="modal fade show"
              style={{ display: 'block' }}
              tabIndex={-1}
            >
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Konfirmasi Selesaikan Ujian</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowFinishingCourse(false)}
                    />
                  </div>
                  <div className="modal-body">
                    <p>
                      Apakah kamu yakin akan review ujian CBT? Kamu tidak
                      bisa mengulang kembali test setelah submit.
                    </p>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowFinishingCourse(false)}
                    >
                      Batal
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={() => {
                        setShowFinishingCourse(false)
                        handleSubmitFlagFinish()
                      }}
                      disabled={submitting}
                    >
                      Ya, Selesaikan Ujian
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {/* Certificate Download Modal */}
        {showCertificateModal && (
          <>
            <div className="modal-backdrop fade show"></div>
            <div
              className="modal fade show"
              style={{ display: 'block' }}
              tabIndex={-1}
            >
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Download Sertifikat</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => setShowCertificateModal(false)}
                    />
                  </div>
                  <div className="modal-body">
                    <p className="mb-3">
                      Apakah Anda yakin ingin mencetak e-sertifikat dengan nama
                      yang tertera pada formulir di bawah ini?
                    </p>
                    <div className="mb-3">
                      <label htmlFor="certificateName" className="form-label">
                        Nama pada Sertifikat
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        id="certificateName"
                        value={certificateName}
                        onChange={(e) => setCertificateName(e.target.value)}
                        placeholder="Masukkan nama lengkap"
                      />
                    </div>
                    <div className="alert alert-warning">
                      <i className="isax isax-warning-2 me-2" />
                      Pastikan nama sudah benar, karena e-sertifikat hanya dapat
                      di generate satu kali.
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setShowCertificateModal(false)}
                    >
                      Batal
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={confirmCertificateDownload}
                      disabled={!certificateName.trim()}
                    >
                      Download Sertifikat
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    )
  }

  const currentQ = questions[currentQuestion]

  // Loading state
  if (loading) {
    return (
      <div className="content">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6">
              <div className="card text-center">
                <div className="card-body">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-3">Memuat data CBT...</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="content">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6">
              <div className="card text-center">
                <div className="card-body">
                  <div className="text-danger mb-3">
                    <i
                      className="isax isax-warning-2"
                      style={{ fontSize: '3rem' }}
                    />
                  </div>
                  <h5 className="text-danger">Error</h5>
                  <p className="mb-4">{error}</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => window.location.reload()}
                  >
                    <i className="isax isax-refresh me-2" />
                    Coba Lagi
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  // No questions available
  if (!questions.length) {
    return (
      <div className="content">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-6">
              <div className="card text-center">
                <div className="card-body">
                  <div className="text-warning mb-3">
                    <i
                      className="isax isax-document-text"
                      style={{ fontSize: '3rem' }}
                    />
                  </div>
                  <h5>Tidak ada soal tersedia</h5>
                  <p className="mb-4">
                    CBT ini belum memiliki soal yang dapat dikerjakan.
                  </p>
                  <button
                    className="btn btn-primary"
                    onClick={handleGoToDashboard}
                  >
                    <i className="isax isax-home me-2" />
                    Kembali ke Dashboard
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="content">
      <div className="container-fluid">
        <div className="row">
          {/* Timer and Progress */}
          <div className="col-12 mb-4">
            <div className="card">
              <div className="card-body">
                <div className="row align-items-center">
                  <div className="col-md-6">
                    <h5 className="mb-0">
                      CBT Test - {cbtData?.title || 'Loading...'}
                    </h5>
                    <small className="text-muted">
                      Soal {currentQuestion + 1} dari {questions.length}
                    </small>
                  </div>
                  <div className="col-md-6 text-end">
                    <div className="d-flex justify-content-end align-items-center gap-3">
                      <div className="text-center">
                        <div className="h4 mb-0 text-warning">
                          {formatTime(timeLeft)}
                        </div>
                        <small className="text-muted">Waktu Tersisa</small>
                      </div>
                      <div
                        className="progress flex-grow-1"
                        style={{ maxWidth: '200px' }}
                      >
                        <div
                          className="progress-bar bg-success"
                          style={{
                            width: `${((currentQuestion + 1) / questions.length) * 100
                              }%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Question */}
          <div className="col-lg-8">
            <div className="card">
              <div className="card-body">

                {currentQ?.image && (
                  <div className="d-flex align-items-center justify-content-center">
                    <img className="img-fluid object-fit-cover mb-3" style={{ maxHeight: '400px' }} src={`${process.env.REACT_APP_BASE_URL_API}/public/storage/${currentQ?.image}`} alt={currentQ?.image} />
                  </div>
                )}

                <h5 className="mb-4 fw-normal" dangerouslySetInnerHTML={{ __html: currentQ.question }}></h5>

                <div className="options-list">
                  {currentQ.answers.map((answer, index) => (
                    <div
                      key={index}
                      className={`option-item p-3 border rounded mb-3 cursor-pointer ${answers[currentQuestion] === index
                        ? 'border-primary bg-light'
                        : ''
                        }`}
                      onClick={() => handleAnswerSelect(index)}
                    >
                      <div className="d-flex align-items-center">
                        <div
                          className={`option-circle me-3 ${answers[currentQuestion] === index
                            ? 'bg-primary text-white'
                            : 'border'
                            }`}
                        >
                          {String.fromCharCode(65 + index)}
                        </div>
                        <span>{answer.choice}</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Navigation Buttons */}
                <div className="d-flex justify-content-between mt-4">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={handlePreviousQuestion}
                    disabled={currentQuestion === 0}
                  >
                    <i className="isax isax-arrow-left me-2" />
                    Sebelumnya
                  </button>

                  <div className="d-flex gap-2">
                    <button
                      className={`btn ${doubtful[currentQuestion]
                        ? 'btn-warning'
                        : 'btn-outline-warning'
                        }`}
                      onClick={() => {
                        const updated = [...doubtful]
                        updated[currentQuestion] = !updated[currentQuestion]
                        setDoubtful(updated)
                      }}
                    >
                      Ragu
                    </button>

                    {currentQuestion === questions.length - 1 ? (
                      <button
                        className="btn btn-success"
                        onClick={() => setShowSubmitConfirm(true)}
                        disabled={submitting}
                      >
                        {submitting ? (
                          <>
                            <span
                              className="spinner-border spinner-border-sm me-2"
                              role="status"
                              aria-hidden="true"
                            ></span>
                            Menyimpan...
                          </>
                        ) : (
                          <>
                            <i className="isax isax-tick-circle me-2" />
                            Selesai & Submit
                          </>
                        )}
                      </button>
                    ) : (
                      <button
                        className="btn btn-primary"
                        onClick={handleNextQuestion}
                      >
                        Selanjutnya
                        <i className="isax isax-arrow-right ms-2" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Question Navigator */}
          <div className="col-lg-4">
            <div className="card">
              <div className="card-header">
                <h6 className="mb-0">Navigasi Soal</h6>
              </div>
              <div className="card-body">
                <div className="row g-2">
                  {questions.map((_, index) => (
                    <div key={index} className="col-3">
                      <button
                        className={`btn btn-sm w-100 ${index === currentQuestion
                          ? 'btn-primary'
                          : doubtful[index]
                            ? 'btn-warning'
                            : answers[index] !== undefined &&
                              answers[index] !== -1
                              ? 'btn-success'
                              : 'btn-outline-secondary'
                          }`}
                        onClick={() => setCurrentQuestion(index)}
                      >
                        {index + 1}
                      </button>
                    </div>
                  ))}
                </div>

                <div className="mt-3">
                  <small className="text-muted">
                    <span className="badge bg-success me-2">●</span> Sudah
                    dijawab
                    <br />
                    <span className="badge bg-warning me-2">●</span> Ragu-ragu
                    <br />
                    <span className="badge bg-primary me-2">●</span> Soal saat
                    ini
                    <br />
                    <span className="badge bg-secondary me-2">●</span> Belum
                    dijawab
                  </small>
                </div>
              </div>
            </div>

            {currentQ?.normal_lab ? (
              <div className="card mt-3">
                <div className="card-header">
                  <h6 className="mb-0">Normal Lab</h6>
                </div>
                <div className="card-body">
                  <button
                    className="btn btn-outline-primary"
                    onClick={() =>
                      window.open(
                        currentQ.normal_lab,
                        '_blank',
                        'noopener,noreferrer',
                      )
                    }
                  >
                    <i className="isax isax-document-text me-2" />
                    Buka Normal Lab
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <style>{`
         .cursor-pointer {
           cursor: pointer;
         }
         .option-circle {
           width: 30px;
           height: 30px;
           border-radius: 50%;
           display: flex;
           align-items: center;
           justify-content: center;
           font-weight: bold;
         }
       `}</style>

      {/* Certificate Download Modal */}
      {showCertificateModal && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div
            className="modal fade show"
            style={{ display: 'block' }}
            tabIndex={-1}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Download Sertifikat</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowCertificateModal(false)}
                  />
                </div>
                <div className="modal-body">
                  <p className="mb-3">
                    Apakah Anda yakin ingin mencetak e-sertifikat dengan nama
                    yang tertera pada formulir di bawah ini?
                  </p>
                  <div className="mb-3">
                    <label htmlFor="certificateName" className="form-label">
                      Nama pada Sertifikat
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="certificateName"
                      value={certificateName}
                      onChange={(e) => setCertificateName(e.target.value)}
                      placeholder="Masukkan nama lengkap"
                    />
                  </div>
                  <div className="alert alert-warning">
                    <i className="isax isax-warning-2 me-2" />
                    Pastikan nama sudah benar, karena e-sertifikat hanya dapat
                    di generate satu kali.
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowCertificateModal(false)}
                  >
                    Batal
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={confirmCertificateDownload}
                    disabled={!certificateName.trim()}
                  >
                    Download Sertifikat
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Submit Confirmation Modal */}
      {showSubmitConfirm && (
        <>
          <div className="modal-backdrop fade show"></div>
          <div
            className="modal fade show"
            style={{ display: 'block' }}
            tabIndex={-1}
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Konfirmasi Selesai Ujian</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowSubmitConfirm(false)}
                  />
                </div>
                <div className="modal-body">
                  <p>
                    Apakah kamu yakin akan mengirim jawaban ujian CBT? Kamu masih
                    bisa mengulangi CBT selama masih ada kesempatan.
                  </p>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowSubmitConfirm(false)}
                  >
                    Batal
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      setShowSubmitConfirm(false)
                      handleFinishTest()
                    }}
                    disabled={submitting}
                  >
                    Ya, Kirim Jawaban
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default CBTTest
